import React, { useState, useEffect } from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const VisionActionList = ({ vision }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (vision?.vision_id) {
      loadActions();
    }
  }, [vision?.vision_id]);

  const loadActions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await axios.post(
        `/api/visions/${vision.vision_id}/generate-strategy-actions`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 30000,
        }
      );

      if (response.data.success) {
        setActions(response.data.actions || []);
      } else {
        setError('Failed to load actions');
      }
    } catch (err) {
      console.error('Error loading actions:', err);
      setError(err.response?.data?.detail || 'Failed to load strategy actions');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    const cls = 'w-5 h-5 text-ink-muted';
    switch (actionType) {
      case 'buy':
        return <ArrowUpIcon className={cls} />;
      case 'sell':
        return <ArrowDownIcon className={cls} />;
      case 'transfer':
      case 'rebalance':
        return <ArrowPathIcon className={cls} />;
      default:
        return <InformationCircleIcon className={cls} />;
    }
  };

  const getPriorityClass = (priority) => {
    if (priority === 'high') return 'border-pine bg-pine-tint';
    return 'border-hairline bg-bone-raised';
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="surface-panel p-6">
        <div className="flex items-center justify-center py-8 gap-3">
          <div className="w-8 h-8 border-2 border-hairline border-t-pine rounded-full animate-spin" />
          <span className="text-body text-ink-muted">Generating strategy actions…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="surface-panel p-6">
        <div className="flex flex-wrap items-center justify-center py-8 gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-warning" />
          <span className="text-body text-ink-muted">{error}</span>
          <button type="button" onClick={loadActions} className="btn-secondary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="surface-panel p-6">
        <div className="text-center py-8">
          <InformationCircleIcon className="w-12 h-12 text-ink-faint mx-auto mb-4" />
          <h3 className="font-display text-h3 text-ink mb-2">No actions available</h3>
          <p className="text-body text-ink-muted mb-4">
            Connect your portfolio and cashflow data to get personalized strategy actions
          </p>
          <button type="button" onClick={loadActions} className="btn-primary">
            Generate actions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-panel p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display text-h3 text-ink mb-1">Strategic actions</h3>
          <p className="text-body text-ink-muted">
            Time-sensitive recommendations to accelerate your goal
          </p>
        </div>
        <button type="button" onClick={loadActions} className="btn-ghost text-sm">
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        <AnimatePresence>
          {actions.map((action, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`border rounded-lg p-4 ${getPriorityClass(action.priority)}`}
            >
              <div className="flex items-start gap-3">
                {getActionIcon(action.action_type)}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-medium text-body text-ink">{action.title}</h4>
                    {action.priority && (
                      <span className="text-label text-ink-faint border border-hairline px-2 py-0.5 rounded-md">
                        {action.priority}
                      </span>
                    )}
                  </div>
                  <p className="text-body text-ink-muted mb-2">{action.description}</p>

                  {action.reason && (
                    <p className="text-label text-ink-faint mb-2 italic">{action.reason}</p>
                  )}

                  <div className="flex flex-wrap gap-4 mt-3 text-label text-ink-muted">
                    {action.ticker && (
                      <span>
                        Ticker: <span className="text-ink">{action.ticker}</span>
                      </span>
                    )}
                    {action.amount && (
                      <span>
                        Amount: <span className="figure text-ink">{formatCurrency(action.amount)}</span>
                      </span>
                    )}
                    {action.deadline && (
                      <span className="flex items-center gap-1">
                        <ClockIcon className="w-4 h-4" />
                        {formatDate(action.deadline)}
                      </span>
                    )}
                    {action.impact_days > 0 && (
                      <span className="text-pine">
                        Reach your goal{' '}
                        <span className="figure">{action.impact_days}</span> days sooner
                      </span>
                    )}
                  </div>

                  {action.source && (
                    <p className="mt-2 pt-2 border-t border-hairline text-label text-ink-faint">
                      Source: <span className="italic">{action.source}</span>
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VisionActionList;
